import React, { useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';
import { sumAccountsBRL } from '../../utils/exchange';

function generateInsights(state) {
  const insights = [];
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

  const curTx   = state.transactions.filter(t => t.date.startsWith(currentMonth) && t.status === 'paid' && !t.cardId);
  const lastTx  = state.transactions.filter(t => t.date.startsWith(lastMonth)    && t.status === 'paid' && !t.cardId);

  const curIncome  = curTx.filter(t => t.type === 'income').reduce((s, t) => s + t.value, 0);
  const curExpense = curTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);
  const lastExpense = lastTx.filter(t => t.type === 'expense').reduce((s, t) => s + t.value, 0);

  // 1. Taxa de poupança
  if (curIncome > 0) {
    const rate = (curIncome - curExpense) / curIncome;
    if (rate >= 0.2) {
      insights.push({ icon: 'fa-piggy-bank', color: '#1d9e75', bg: '#d1fae5',
        text: `Parabéns! Você está poupando ${Math.round(rate * 100)}% da sua renda este mês.` });
    } else if (rate < 0) {
      insights.push({ icon: 'fa-triangle-exclamation', color: '#e24b4a', bg: '#fee2e2',
        text: `Atenção: seus gastos (${formatCurrency(curExpense)}) superam sua renda (${formatCurrency(curIncome)}) este mês.` });
    } else {
      insights.push({ icon: 'fa-chart-simple', color: '#f59e0b', bg: '#fef3c7',
        text: `Sua taxa de poupança é de ${Math.round(rate * 100)}% este mês. Tente aumentar para pelo menos 20%.` });
    }
  }

  // 2. Comparação com mês anterior
  if (lastExpense > 0 && curExpense > 0) {
    const diff = ((curExpense - lastExpense) / lastExpense) * 100;
    if (diff > 15) {
      insights.push({ icon: 'fa-arrow-trend-up', color: '#f97316', bg: '#ffedd5',
        text: `Seus gastos aumentaram ${Math.round(diff)}% comparado ao mês passado (${formatCurrency(curExpense)} vs ${formatCurrency(lastExpense)}).` });
    } else if (diff < -10) {
      insights.push({ icon: 'fa-arrow-trend-down', color: '#1d9e75', bg: '#d1fae5',
        text: `Você reduziu seus gastos em ${Math.round(Math.abs(diff))}% em relação ao mês passado. Continue assim!` });
    }
  }

  // 3. Maior categoria de gasto
  const catMap = {};
  curTx.filter(t => t.type === 'expense').forEach(t => { catMap[t.categoryId] = (catMap[t.categoryId] || 0) + t.value; });
  const topEntry = Object.entries(catMap).sort((a, b) => b[1] - a[1])[0];
  if (topEntry && curExpense > 0) {
    const topCat = state.categories.find(c => c.id === topEntry[0]);
    const pct = Math.round((topEntry[1] / curExpense) * 100);
    if (pct >= 30) {
      insights.push({ icon: 'fa-tag', color: '#8b5cf6', bg: '#f3e8ff',
        text: `${topCat?.name || 'Uma categoria'} representa ${pct}% dos seus gastos este mês (${formatCurrency(topEntry[1])}).` });
    }
  }

  // 4. Orçamento ultrapassado
  if (state.budgets.length > 0) {
    const exceeded = state.budgets.filter(b => (catMap[b.categoryId] || 0) > b.planned);
    if (exceeded.length > 0) {
      const cat = state.categories.find(c => c.id === exceeded[0].categoryId);
      insights.push({ icon: 'fa-circle-exclamation', color: '#e24b4a', bg: '#fee2e2',
        text: `Orçamento de ${cat?.name || 'categoria'} ultrapassado este mês. Revise seus gastos nessa área.` });
    }
  }

  // 5. Contas em atraso
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const overdue = state.transactions.filter(
    t => t.status === 'pending' && t.type === 'expense' && new Date(t.date + 'T00:00:00') < today
  );
  if (overdue.length > 0) {
    const total = overdue.reduce((s, t) => s + t.value, 0);
    insights.push({ icon: 'fa-clock', color: '#e24b4a', bg: '#fee2e2',
      text: `Você tem ${overdue.length} conta${overdue.length > 1 ? 's' : ''} em atraso totalizando ${formatCurrency(total)}.` });
  }

  // 6. Reserva de emergência
  const totalBalance = sumAccountsBRL(state.accounts, state.usdRate);
  let avgExp = 0, cnt = 0;
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const e = state.transactions.filter(t => t.date.startsWith(m) && t.type === 'expense' && t.status === 'paid' && !t.cardId).reduce((s, t) => s + t.value, 0);
    if (e > 0) { avgExp += e; cnt++; }
  }
  if (cnt > 0) { avgExp /= cnt; }
  else if (curExpense > 0) { avgExp = curExpense; }

  if (avgExp > 0) {
    const coverage = totalBalance / avgExp;
    if (coverage < 3 && totalBalance >= 0) {
      insights.push({ icon: 'fa-shield-halved', color: '#f97316', bg: '#ffedd5',
        text: `Sua reserva de emergência cobre apenas ${coverage.toFixed(1)} meses de gastos. O ideal é ter 3 a 6 meses.` });
    } else if (coverage >= 6) {
      insights.push({ icon: 'fa-shield-check', color: '#1d9e75', bg: '#d1fae5',
        text: `Reserva de emergência excelente: ${coverage.toFixed(1)} meses de gastos cobertos!` });
    }
  }

  // 7. Anomalia por categoria: gasto muito acima da média dos últimos 3 meses
  const catAvg = {};
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    state.transactions
      .filter(t => t.date.startsWith(m) && t.type === 'expense' && t.status === 'paid' && !t.cardId)
      .forEach(t => {
        if (!catAvg[t.categoryId]) catAvg[t.categoryId] = { sum: 0, months: new Set() };
        catAvg[t.categoryId].sum += t.value;
        catAvg[t.categoryId].months.add(m);
      });
  }
  const anomalies = Object.entries(catMap)
    .map(([catId, curVal]) => {
      const hist = catAvg[catId];
      if (!hist || hist.months.size < 2) return null; // precisa de histórico
      const avg = hist.sum / hist.months.size;
      if (avg <= 0) return null;
      const pctAbove = ((curVal - avg) / avg) * 100;
      return pctAbove >= 60 && (curVal - avg) >= 150 ? { catId, curVal, avg, pctAbove } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.pctAbove - a.pctAbove);
  if (anomalies.length > 0) {
    const a = anomalies[0];
    const cat = state.categories.find(c => c.id === a.catId);
    insights.push({ icon: 'fa-magnifying-glass-chart', color: '#e24b4a', bg: '#fee2e2',
      text: `Gasto fora do padrão: ${cat?.name || 'uma categoria'} está ${Math.round(a.pctAbove)}% acima da sua média (${formatCurrency(a.curVal)} vs média de ${formatCurrency(a.avg)}).` });
  }

  return insights.slice(0, 5);
}

export default function AutoInsights() {
  const { state } = useApp();
  const insights = useMemo(() => generateInsights(state), [state]);

  if (insights.length === 0) return null;

  return (
    <div className="card" style={{ padding: '20px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: '#e6f1fb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className="fas fa-lightbulb" style={{ color: '#378ADD', fontSize: 15 }}></i>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Insights Automáticos</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Análise personalizada do seu perfil financeiro</div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {insights.map((ins, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '10px 12px', background: ins.bg,
            borderRadius: 10, border: `1px solid ${ins.color}22`,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8,
              background: `${ins.color}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: 1,
            }}>
              <i className={`fas ${ins.icon}`} style={{ color: ins.color, fontSize: 12 }}></i>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>{ins.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
